import { type ReactNode, useRef } from "react";

import type { DropDownProps } from "antd/lib/dropdown";

import { IconCaretSmallDown } from "@btnet/blueprint-icons";
import { cn } from "@btnet/utilities-core";

import { BuiButton, type TBuiButtonTypes } from "../BuiButton/BuiButton";
import { BuiDropdown } from "../BuiDropdown/BuiDropdown";
import { BuiMenu } from "../BuiMenu/BuiMenu";
import "./BuiSplitButton.less";

/** A single action used by BuiSplitButton (the primary button or a menu item). */
export interface IBuiSplitButtonItem {
  "data-testid": string;
  onClick: () => Promise<void> | void;
  disabled?: boolean;
  content: ReactNode;
  "aria-label"?: string;
  icon?: ReactNode;
}

/**
 * BuiSplitButton props - decorative composition of BuiButton + BuiDropdown + BuiMenu.
 *
 * This is the decorative `Bui*` layer: no analytics, hotkeys, business logic, or app
 * context. Cross-layer concerns (analytics, hotkeys, loading resolution) belong in the
 * core `BTSplitButton` wrapper.
 *
 * A split button is a primary action (`buttonAction`, the left button) plus a caret
 * button that opens a menu of `menuItems`. When `menuItems` is absent, empty, or every
 * item is disabled, it renders as a standalone button (parity with the legacy
 * BTSplitButton).
 */
export interface IBuiSplitButtonProps {
  "data-testid": string;

  /**
   * The primary action rendered as the left button. Its `content` is the label and its
   * `onClick` fires when the main button is clicked.
   */
  buttonAction: IBuiSplitButtonItem;

  /**
   * Secondary actions shown in the caret dropdown. If omitted, empty, or all-disabled,
   * no dropdown is rendered and only the primary button is shown.
   */
  menuItems?: IBuiSplitButtonItem[];

  /**
   * @default "secondary"
   */
  type?: TBuiButtonTypes;

  className?: string;

  /**
   * Disables both the primary button and the dropdown trigger.
   * @default false
   */
  disabled?: boolean;

  /**
   * Renders the primary and dropdown-trigger buttons in a loading/disabled state. The
   * core wrapper resolves `actionBeingPerformed`/`loadingAction` into this boolean.
   * @default false
   */
  loading?: boolean;

  /**
   * Placement of the dropdown menu, passed through to BuiDropdown (antd placement).
   * @default "bottomRight"
   */
  placement?: DropDownProps["placement"];

  /**
   * When true, the primary button hides its label and shows only its icon (no effect
   * without an icon). This is the already-resolved state — the core `BTSplitButton`
   * wrapper owns the media-query breakpoint check, keeping this decorative layer free of
   * environment concerns.
   * @default false
   */
  collapse?: boolean;

  /**
   * Pure passthrough to the underlying dropdown. The consuming layer supplies an
   * app-specific resolver (e.g. closest modal) — this decorative layer must not import
   * app code.
   */
  getPopupContainer?: DropDownProps["getPopupContainer"];

  /**
   * Passthrough to the underlying dropdown: when `false`, focus is not returned to the caret
   * trigger when the menu closes (focus-into-menu on open and Escape-to-close still work). Set
   * to `false` when a menu action swaps in a focus-owning element (e.g. an inline input) that
   * the close focus-return would otherwise steal focus from.
   * @default true
   */
  returnFocusOnClose?: boolean;
}

/**
 * BuiSplitButton component - a decorative split button: a primary action button (left)
 * plus a caret button that opens a menu of secondary actions.
 *
 * This is the decorative `Bui*` layer: no analytics, hotkeys, business logic, or app
 * context. Cross-layer concerns belong in the core `BTSplitButton` wrapper.
 *
 * Behavior:
 * - When `menuItems` is absent, empty, or every item is disabled, renders a standalone
 *   primary button (parity with the legacy BTSplitButton).
 * - Otherwise renders the primary button joined to a caret trigger that opens the menu.
 *   Focus returns to the caret trigger when the menu closes (via BuiDropdown's
 *   `triggerRef`).
 * @param props - The component props.
 * @example
 * ```tsx
 * <BuiSplitButton
 *   data-testid="save-split"
 *   buttonAction={{ "data-testid": "save", content: "Save", onClick: handleSave }}
 *   menuItems={[
 *     { "data-testid": "save-new", content: "Save and new", onClick: handleSaveNew },
 *     { "data-testid": "save-close", content: "Save and close", onClick: handleSaveClose },
 *   ]}
 * />
 * ```
 */
export const BuiSplitButton = (props: IBuiSplitButtonProps) => {
  const {
    "data-testid": testid,
    buttonAction,
    menuItems,
    type = "secondary",
    className,
    disabled,
    loading = false,
    placement = "bottomRight",
    collapse = false,
    getPopupContainer,
    returnFocusOnClose,
  } = props;

  const triggerRef = useRef<HTMLElement>(null);

  // Render the caret whenever menu items exist, even if every item is disabled — the
  // menu still opens so users can see which actions would be available. Individual items
  // render disabled via their own `disabled` prop. Only a truly empty menu collapses to
  // a standalone button.
  const hasMenu = !!menuItems?.length;

  const shouldCollapse = collapse && !!buttonAction.icon;

  const primaryButton = (
    <BuiButton
      type={type}
      testid={buttonAction["data-testid"]}
      onClick={buttonAction.onClick}
      disabled={(disabled ?? false) || loading}
      loading={loading}
      icon={buttonAction.icon}
      aria-label={buttonAction["aria-label"]}
      className={cn(className, { leftButton: hasMenu })}
    >
      {!shouldCollapse && buttonAction.content}
    </BuiButton>
  );

  if (!hasMenu) {
    return primaryButton;
  }

  const actions = menuItems.map((x) => (
    <BuiMenu.Item
      key={x["data-testid"]}
      testid={x["data-testid"]}
      onClick={x.onClick}
      disabled={x.disabled ?? false}
      aria-label={x["aria-label"]}
    >
      {x.content}
    </BuiMenu.Item>
  ));

  return (
    <div className="BuiSplitButton" data-testid={testid}>
      {primaryButton}
      <BuiDropdown
        testid={`${testid}-split-button-dropdown`}
        triggerRef={triggerRef}
        placement={placement}
        disabled={(disabled ?? false) || loading}
        {...(returnFocusOnClose !== undefined ? { returnFocusOnClose } : {})}
        {...(getPopupContainer ? { getPopupContainer } : {})}
        overlay={
          <BuiMenu
            aria-label="Related actions menu"
            testid={`${testid}-splitButtonMenu`}
          >
            {actions}
          </BuiMenu>
        }
      >
        <BuiButton
          ref={triggerRef}
          type={type}
          testid={`${testid}-rightButton`}
          aria-label="Related actions"
          disabled={(disabled ?? false) || loading}
          className={cn(className, "rightButton")}
          icon={<IconCaretSmallDown />}
        />
      </BuiDropdown>
    </div>
  );
};
